import {
  ArrowUturnLeftIcon,
  MinusCircleIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import {
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { IconSize } from '@constants';
import { JTextFieldBaseProps } from '@atoms/JTextFieldBase';
import { JTextFieldBaseStyled } from './j-number-field-styled';
import { useTranslation } from 'react-i18next';

export interface Props extends JTextFieldBaseProps {
  onNumberChange: (value: number) => void;
  reset?: boolean;
  hideButtons?: boolean;
  smallSize?: boolean;
  newValue?: string | number;
}

const JNumberField = ({
  onNumberChange,
  defaultValue,
  newValue,
  reset,
  disabled,
  hideButtons,
  smallSize,
  ...props
}: Props) => {
  const [value, setValue] = useState<number | undefined>(
    Number(newValue ?? defaultValue),
  );
  const { t } = useTranslation();
  const theme = useTheme();
  const handleChange = (inputValue: string) => {
    // Check if the input is a valid number

    if (inputValue === '') {
      setValue(0);
      return;
    }
    const numericValue = parseFloat(inputValue);

    if (!isNaN(numericValue) && numericValue >= 0) {
      setValue(numericValue);
    }
  };

  const handleReset = () => {
    setValue(Number(defaultValue));
  };

  useEffect(() => {
    handleChange(String(newValue ?? defaultValue));
  }, [defaultValue, newValue]);

  useEffect(() => {
    if (reset) {
      handleReset();
    }
  }, [reset]);

  return (
    <JTextFieldBaseStyled
      disabled={disabled}
      type="number"
      focused={!!value || value === 0}
      defaultValue={defaultValue}
      value={value}
      attrs={{ smallSize }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="start">
            {!disabled && !hideButtons && (
              <Stack direction={'row'} alignItems={'center'}>
                <IconButton
                  onClick={() => {
                    onNumberChange(value && value > 0 ? value - 1 : 0);
                    if (!value) setValue(0);
                    else if (value > 0) setValue(value - 1);
                  }}
                >
                  <MinusCircleIcon
                    color={theme.palette.dark.dark_1}
                    height={IconSize.buttonIcon}
                  />
                </IconButton>
                <IconButton
                  onClick={() => {
                    onNumberChange(value ? value + 1 : 0);
                    if (value === undefined) setValue(0);
                    else setValue(value + 1);
                  }}
                >
                  <PlusCircleIcon
                    color={theme.palette.dark.dark_1}
                    height={IconSize.buttonIcon}
                  />
                </IconButton>
                <Tooltip title={t('RESET_TO_DEFAULT')}>
                  <IconButton
                    onClick={() => {
                      onNumberChange(Number(defaultValue));
                      setValue(Number(defaultValue));
                    }}
                  >
                    <ArrowUturnLeftIcon
                      color={theme.palette.dark.dark_1}
                      height={IconSize.buttonIcon}
                    />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </InputAdornment>
        ),
      }}
      {...props}
      onChange={(ev) => {
        handleChange(ev.target.value);
        onNumberChange(Number(ev.target.value));
      }}
    />
  );
};

export default JNumberField;
